// src/pages/CatalogMigrationPage.tsx
// Catalog Migration cockpit — drive the smart catalog import: stage → select →
// materialize → release (datasheet acquisition), with run/pause/stop + live stats.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, MenuItem, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, Menu, Switch, FormControlLabel,
  Stack, Divider, LinearProgress, CircularProgress, Snackbar, Alert, Tooltip, IconButton, TablePagination,
  Checkbox, InputAdornment, Link, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import BuildIcon from '@mui/icons-material/Build';
import DownloadingIcon from '@mui/icons-material/Downloading';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import catalogImportService, {
  CatalogJobStats, CatalogRow, PreviewResult, JobSummary,
} from '../services/catalogImportService';

const LAST_JOB_KEY = 'catalogMigration.lastJob';

const CORE_FIELDS = [
  'partNumber', 'manufacturerPartNumber', 'manufacturerName', 'modelNumber', 'description', 'unspscCode',
];
const FIELD_LABEL: Record<string, string> = {
  partNumber: 'Part Number (customer)', manufacturerPartNumber: 'MPN', manufacturerName: 'Manufacturer',
  modelNumber: 'Model Number', description: 'Description', unspscCode: 'UNSPSC Code',
};

const TIERS = ['A', 'B', 'C', 'D', 'E'];
const TIER_COLOR: Record<string, string> = {
  A: '#0EA5E9', B: '#3B82F6', C: '#9333EA', D: '#F59E0B', E: '#64748B',
  CLARIFICATION: '#94A3B8',
};
const STATUS_COLOR: Record<string, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  STAGED: 'default', SELECTED: 'info', MATERIALIZED: 'info',
  ENRICHING: 'warning', DONE: 'success', FAILED: 'error',
  NO_DATASHEET: 'warning',
  HELD: 'default', SKIPPED: 'default', CLARIFICATION: 'default',
};

const num = (n?: number) => (n ?? 0).toLocaleString();

export default function CatalogMigrationPage() {
  const [loadedJob, setLoadedJob] = useState(() => localStorage.getItem(LAST_JOB_KEY) || '');
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [stats, setStats] = useState<CatalogJobStats | null>(null);
  // Rows the worker is on RIGHT NOW + when the last poll landed. Without these
  // the page looks frozen during a release: the counters only move when a part
  // finishes, which is 20-60s apart.
  const [inFlight, setInFlight] = useState<CatalogRow[]>([]);
  const [lastPoll, setLastPoll] = useState<Date | null>(null);
  const doneHistory = useRef<Array<{ t: number; done: number }>>([]);
  // upload → mapping preview → confirm
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [jobName, setJobName] = useState('');
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [rows, setRows] = useState<CatalogRow[]>([]);
  const [rowsTotal, setRowsTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [rowTier, setRowTier] = useState('');
  const [rowStatus, setRowStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);

  // selection (checkbox) + search
  const [rowSearch, setRowSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Scope: run on the checkbox selection, or on the whole current filter.
  const [scope, setScope] = useState<'selected' | 'all'>('all');
  // release settings
  const [concurrency, setConcurrency] = useState(3);
  const [fullLadder, setFullLadder] = useState(false);
  const [retryMisses, setRetryMisses] = useState(false);
  // Which stage the Run button runs across the whole list, batch by batch.
  const [runMode, setRunMode] = useState<'materialize' | 'release'>('materialize');
  const [menuEl, setMenuEl] = useState<null | HTMLElement>(null);
  const timer = useRef<number | null>(null);

  const notify = (msg: string, sev: 'success' | 'error' | 'info' = 'info') => setToast({ msg, sev });

  const refresh = useCallback(async (id: string) => {
    if (!id) return;
    try {
      const [s, r, live] = await Promise.all([
        catalogImportService.stats(id),
        catalogImportService.rows(id, {
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          tier: rowTier || undefined,
          status: rowStatus || undefined,
          search: rowSearch || undefined,
        }),
        catalogImportService.rows(id, { limit: 10, offset: 0, status: 'ENRICHING' }),
      ]);
      setStats(s);
      setRows(r.rows);
      setRowsTotal(r.total);
      setInFlight(live.rows || []);
      setLastPoll(new Date());
      // Keep a 10-minute window of DONE counts so we can show a real rate/ETA
      // instead of a percentage of the whole file that barely moves.
      const doneNow = (s.byStatus?.DONE || 0) + (s.byStatus?.NO_DATASHEET || 0);
      const hist = doneHistory.current;
      hist.push({ t: Date.now(), done: doneNow });
      while (hist.length > 1 && Date.now() - hist[0].t > 600_000) hist.shift();
    } catch (e: any) {
      notify(e?.response?.data?.message || e.message || 'load failed', 'error');
    }
  }, [page, rowsPerPage, rowTier, rowStatus, rowSearch]);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const p = await catalogImportService.uploadPreview(file);
      setPreview(p);
      setJobName(file.name.replace(/\.[^.]+$/, ''));
      // seed the editable mapping from the auto-detected suggestions
      setMapping(Object.fromEntries(p.mapping.map((m) => [m.targetField, m.sourceColumn])));
      notify(`Parsed ${p.totalRows.toLocaleString()} rows · ${p.mapping.length} fields auto-mapped`, 'success');
    } catch (err: any) {
      notify(err?.response?.data?.message || err.message || 'upload failed', 'error');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const confirmImport = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const res = await catalogImportService.uploadIngest({
        filePath: preview.filePath,
        originalFileName: preview.originalFileName,
        jobName: jobName || preview.originalFileName,
        sheetName: preview.sheetName,
        mapping,
      });
      notify(`Imported ${res.totalRows.toLocaleString()} rows → job ${res.jobId}`, 'success');
      setPreview(null);
      setLoadedJob(res.jobId);
      loadJobs();
      await refresh(res.jobId);
    } catch (err: any) {
      notify(err?.response?.data?.message || err.message || 'import failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const loadJobs = useCallback(() => {
    catalogImportService.listJobs().then(setJobs).catch(() => {});
  }, []);
  useEffect(() => { loadJobs(); }, [loadJobs]);

  const doDeleteJob = async (e: React.MouseEvent, jobId: string) => {
    e.stopPropagation();
    if (!window.confirm(`Delete import ${jobId}?\nStaged rows are removed; any Parts already created are kept.`)) return;
    try {
      await catalogImportService.deleteJob(jobId);
      if (loadedJob === jobId) { setLoadedJob(''); setStats(null); }
      loadJobs();
      notify('Import deleted', 'success');
    } catch (err: any) {
      notify(err?.response?.data?.message || err.message || 'delete failed', 'error');
    }
  };

  // persist the loaded job so a refresh / re-open restores it
  useEffect(() => {
    if (loadedJob) localStorage.setItem(LAST_JOB_KEY, loadedJob);
    else localStorage.removeItem(LAST_JOB_KEY);
  }, [loadedJob]);

  // auto-refresh while a job is loaded (so release progress updates live);
  // also re-fetches immediately when the page/filters change (refresh identity).
  useEffect(() => {
    if (!loadedJob) return;
    refresh(loadedJob);
    timer.current = window.setInterval(() => refresh(loadedJob), 5000);
    return () => { if (timer.current) window.clearInterval(timer.current); };
  }, [loadedJob, refresh]);

  const run = async (fn: () => Promise<string>) => {
    setBusy(true);
    try {
      notify(await fn(), 'success');
      await refresh(loadedJob);
    } catch (e: any) {
      notify(e?.response?.data?.message || e.message || 'action failed', 'error');
    } finally {
      setBusy(false);
    }
  };

  const selectionCount = selected.size;
  const clearSelection = () => { setSelected(new Set()); setScope('all'); };

  const doControl = (a: 'pause' | 'stop') => run(async () => {
    const { controlState } = await catalogImportService.control(loadedJob, a);
    return `Control: ${controlState}`;
  });

  // The single Run action: run the chosen MODE on the chosen SCOPE. The system
  // then works the WHOLE set batch by batch in the background until done (or you
  // pause/stop), resuming from wherever the counts left off.
  //  - Mode 'materialize' → create Parts from STAGED rows (fast, ~free).
  //  - Mode 'release'     → acquire datasheets for MATERIALIZED rows (optional
  //    full-ladder search / retry-misses).
  //  - Scope 'selected'   → just the checkbox selection.
  //  - Scope 'all'        → the entire current filter (tier + search).
  const doRun = () => run(async () => {
    if (scope === 'selected' && selected.size === 0) throw new Error('No rows selected');
    const rowIds = scope === 'selected' ? [...selected] : undefined;
    const filter = scope === 'all'
      ? { tier: rowTier || undefined, search: rowSearch || undefined, all: true as const }
      : { rowIds, limit: rowIds!.length };

    if (runMode === 'materialize') {
      const r = await catalogImportService.materialize(loadedJob, filter as any);
      if (scope === 'all' && (r.matching ?? 0) === 0) {
        throw new Error('Nothing to materialize — no STAGED rows match this filter (they may already be materialized).');
      }
      clearSelection();
      return scope === 'all'
        ? `Materializing ${(r.matching ?? 0).toLocaleString()} staged parts, batch by batch — watch the tiles.`
        : `Materialized ${r.materialized ?? 0} parts`;
    }
    const r = await catalogImportService.release(loadedJob, { ...(filter as any), concurrency, fullLadder, retryMisses });
    if (scope === 'all' && (r.matching ?? 0) === 0) {
      throw new Error(
        retryMisses
          ? 'Nothing to release — no materialized or no-datasheet rows match this filter.'
          : 'Nothing to release — these rows are already done or missed. Turn on “Retry misses” to re-run the No-datasheet ones.',
      );
    }
    clearSelection();
    return scope === 'all'
      ? `Releasing ${(r.matching ?? 0).toLocaleString()} ${retryMisses ? 'materialized + missed' : 'materialized'} parts, ${concurrency} at a time — watch the tiles.`
      : `Released ${r.released} · datasheets ${r.registered} · misses ${r.misses}`;
  });

  const toggleRow = (id: string) => { setScope('selected'); setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  }); };
  const togglePage = (checked: boolean) => {
    setScope('selected');
    setSelected((s) => {
      const n = new Set(s);
      rows.forEach((r) => (checked ? n.add(r._id) : n.delete(r._id)));
      return n;
    });
  };
  const applySearch = () => { setRowSearch(searchDraft.trim()); setPage(0); clearSelection(); };

  const pageAllChecked = rows.length > 0 && rows.every((r) => selected.has(r._id));
  const releaseActive = !!stats?.releaseActive;
  const byStatus = stats?.byStatus || {};
  const done = byStatus.DONE || 0;
  const enriching = byStatus.ENRICHING || 0;
  const noDatasheet = byStatus.NO_DATASHEET || 0;
  const failed = byStatus.FAILED || 0;
  const materialized = (byStatus.MATERIALIZED || 0) + done + enriching + noDatasheet;
  const total = stats?.total || 0;
  // Progress against the cohort that has actually been RELEASED (settled +
  // in flight + waiting), not the whole file. Measuring 25 wins against 14,727
  // staged rows reads as 0% forever and hides real throughput.
  const settled = done + noDatasheet + failed;
  const releasedCohort = settled + enriching;
  const progress = releasedCohort ? Math.round((done / releasedCohort) * 100) : 0;
  const queued = byStatus.MATERIALIZED || 0;
  // parts/min over the polling window, and an ETA for what is still queued.
  const hist = doneHistory.current;
  const rate = (() => {
    if (hist.length < 2) return 0;
    const first = hist[0], last = hist[hist.length - 1];
    const mins = (last.t - first.t) / 60_000;
    return mins > 0.5 ? (last.done - first.done) / mins : 0;
  })();
  const etaMin = rate > 0 && queued > 0 ? Math.round(queued / rate) : 0;

  const tiles = useMemo(() => ([
    { k: 'Total', v: total, c: '#64748B' },
    { k: 'Staged', v: byStatus.STAGED || 0, c: '#94A3B8' },
    { k: 'Materialized', v: materialized, c: '#3B82F6' },
    { k: 'Enriching', v: enriching, c: '#F59E0B' },
    { k: 'No datasheet', v: noDatasheet, c: '#D97706' },
    { k: 'Done', v: done, c: '#10B981' },
    { k: 'Failed', v: failed, c: '#EF4444' },
  ]), [stats]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ p: 3, maxWidth: 1280, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>Catalog Migration</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Stage a customer catalog into parts, then release it into datasheet acquisition — under your control.
      </Typography>

      {/* Upload starter */}
      <Card variant="outlined" sx={{ mb: 2 }}>
        <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <input ref={fileInput} type="file" accept=".xlsx,.xls,.csv" hidden onChange={onFile} />
          <Button variant="contained" startIcon={<UploadFileIcon />} onClick={() => fileInput.current?.click()} disabled={busy}>
            Upload catalog file
          </Button>
          <Typography variant="body2" color="text.secondary">
            Upload an .xlsx to start a migration
          </Typography>
        </CardContent>
      </Card>

      {busy && <LinearProgress sx={{ mb: 1 }} />}

      {/* Mapping preview — verify/adjust before staging (like BOM import) */}
      {preview && (
        <Card variant="outlined" sx={{ mb: 2, borderColor: 'primary.main' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Confirm mapping — {preview.originalFileName} · {preview.totalRows.toLocaleString()} rows · sheet "{preview.sheetName}"
            </Typography>
            <TextField size="small" label="Job name" value={jobName} onChange={(e) => setJobName(e.target.value)} sx={{ mb: 2, minWidth: 280 }} />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mb: 2 }}>
              {CORE_FIELDS.map((f) => (
                <TextField key={f} select size="small" label={FIELD_LABEL[f]} value={mapping[f] || ''}
                  onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value }))}>
                  <MenuItem value=""><em>— none —</em></MenuItem>
                  {preview.columns.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              ))}
            </Box>
            {/* Auto-detected multi-column groups (not 1:1 mapped) */}
            <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap' }} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>AUTO-DETECTED</Typography>
              <Chip size="small" color={preview.detected.attributePairs ? 'success' : 'default'}
                label={`${preview.detected.attributePairs} attribute pairs → specifications`} />
              <Chip size="small" label={`${preview.detected.referenceUrlColumns} reference-URL columns`} />
              <Chip size="small" label={`${preview.detected.classificationColumns} classification columns`} />
              {preview.detected.attributeExamples.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  e.g. {preview.detected.attributeExamples.join(', ')}
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">Sample (first rows, as auto-mapped):</Typography>
            <Box sx={{ overflowX: 'auto', mb: 2 }}>
              <Table size="small">
                <TableHead><TableRow>
                  {['Part #', 'MPN', 'Manufacturer', 'Description', 'Specs'].map((h) => <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>)}
                </TableRow></TableHead>
                <TableBody>
                  {preview.sample.map((s, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{s.partNumber || '—'}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{s.manufacturerPartNumber || '—'}</TableCell>
                      <TableCell>{s.manufacturerName || '—'}</TableCell>
                      <TableCell>{s.description || '—'}</TableCell>
                      <TableCell>{s.specsDetected > 0 ? <Chip size="small" color="success" label={s.specsDetected} /> : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="contained" onClick={confirmImport} disabled={busy || !mapping.partNumber}>Confirm &amp; Import</Button>
              <Button onClick={() => setPreview(null)} disabled={busy}>Cancel</Button>
              {!mapping.partNumber && <Typography variant="caption" color="error">Part Number mapping is required</Typography>}
            </Stack>
          </CardContent>
        </Card>
      )}

      {loadedJob && stats && (
        <>
          {/* Job header + settings/overflow menu */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>Migration in progress</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{loadedJob}</Typography>
            </Box>
            <Box flex={1} />
            <Tooltip title="Job options">
              <IconButton onClick={(e) => setMenuEl(e.currentTarget)}><MoreVertIcon /></IconButton>
            </Tooltip>
            <Menu anchorEl={menuEl} open={!!menuEl} onClose={() => setMenuEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
              <MenuItem onClick={() => { refresh(loadedJob); setMenuEl(null); }}>
                <RefreshIcon fontSize="small" sx={{ mr: 1 }} /> Refresh now
              </MenuItem>
              <MenuItem onClick={() => { setLoadedJob(''); setStats(null); setMenuEl(null); }}>
                Close migration
              </MenuItem>
            </Menu>
          </Box>

          {/* Stat tiles */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 1, mb: 2 }}>
            {tiles.map((t) => (
              <Card key={t.k} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                    <Box component="span" sx={{ display: 'inline-block', width: 8, height: 8, borderRadius: '2px', bgcolor: t.c, mr: 0.75 }} />
                    {t.k}
                  </Typography>
                  <Typography variant="h5" fontWeight={600}>{num(t.v)}</Typography>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Tier breakdown + progress */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mr: 1 }}>TIERS</Typography>
                {Object.entries(stats.byTier).sort().map(([k, v]) => (
                  <Chip key={k} size="small" label={`${k} · ${num(v)}`}
                    sx={{ bgcolor: `${TIER_COLOR[k] || '#64748B'}22`, color: TIER_COLOR[k] || '#64748B', fontWeight: 600 }} />
                ))}
              </Stack>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box flex={1}><LinearProgress variant={releaseActive && releasedCohort === 0 ? 'indeterminate' : 'determinate'} value={progress} color="success" /></Box>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {releasedCohort > 0
                    ? `${num(done)} / ${num(releasedCohort)} released done (${progress}%)`
                    : `nothing released yet — ${num(queued)} materialized and waiting`}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Live strip — what the worker is on RIGHT NOW. The counters only move
              when a part settles (20-60s), so without this the page reads as stuck. */}
          {(releaseActive || inFlight.length > 0) && (
            <Card variant="outlined" sx={{ mb: 2, borderColor: '#F59E0B55', bgcolor: '#F59E0B08' }}>
              <CardContent sx={{ py: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <CircularProgress size={16} thickness={5} sx={{ color: '#F59E0B' }} />
                  <Typography variant="body2" fontWeight={600}>
                    {inFlight.length > 0 ? `Working on ${inFlight.length}` : 'Waiting for the next part'}
                  </Typography>
                  {inFlight.map((r) => (
                    <Chip key={r._id} size="small" variant="outlined"
                      label={`${r.materialNumber} · ${r.manufacturerPartNumber || '—'}`} />
                  ))}
                  <Box flex={1} />
                  {rate > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {rate.toFixed(1)} parts/min{etaMin > 0 ? ` · ~${etaMin} min for ${num(queued)} queued` : ''}
                    </Typography>
                  )}
                  {lastPoll && (
                    <Typography variant="caption" color="text.secondary">
                      updated {lastPoll.toLocaleTimeString()}
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Action bar — Mode · Batch · Scope · Run/Pause/Stop */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', py: 1.5 }}>
              {/* MODE / step */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Step</Typography>
                <ToggleButtonGroup size="small" exclusive value={runMode} onChange={(_, v) => v && setRunMode(v)}>
                  <ToggleButton value="materialize">
                    <Tooltip title="Create catalog Parts from staged rows (fast, ~free)"><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><BuildIcon fontSize="small" /> Materialize</Box></Tooltip>
                  </ToggleButton>
                  <ToggleButton value="release">
                    <Tooltip title="Acquire datasheets for materialized Parts (batched, background)"><Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><DownloadingIcon fontSize="small" /> Release</Box></Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {/* BATCH + release options (release only) */}
              {runMode === 'release' && (
                <>
                  <Tooltip title="Parts processed in parallel per wave">
                    <TextField size="small" type="number" label="Batch" value={concurrency}
                      onChange={(e) => setConcurrency(Math.max(1, Math.min(12, +e.target.value)))}
                      disabled={releaseActive} sx={{ width: 84 }} inputProps={{ min: 1, max: 12 }} />
                  </Tooltip>
                  <FormControlLabel sx={{ m: 0 }}
                    control={<Switch size="small" checked={fullLadder} onChange={(e) => setFullLadder(e.target.checked)} />}
                    label={<Tooltip title="On a miss, also scrape the manufacturer site and run grounded search — costs more"><Typography variant="caption">Full ladder ($)</Typography></Tooltip>} />
                  <FormControlLabel sx={{ m: 0 }}
                    control={<Switch size="small" checked={retryMisses} onChange={(e) => setRetryMisses(e.target.checked)} />}
                    label={<Tooltip title="Re-run parts that found no datasheet — pair with Full ladder"><Typography variant="caption">Retry misses</Typography></Tooltip>} />
                </>
              )}

              <Box flex={1} />

              {/* SCOPE */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>Run on</Typography>
                <ToggleButtonGroup size="small" exclusive value={scope} onChange={(_, v) => v && setScope(v)}>
                  <ToggleButton value="selected" disabled={selected.size === 0}>Selected ({selectionCount.toLocaleString()})</ToggleButton>
                  <ToggleButton value="all">All filtered ({rowsTotal.toLocaleString()})</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Divider orientation="vertical" flexItem />

              {/* STATUS + TRANSPORT */}
              <Chip size="small" color={releaseActive ? 'success' : 'default'} variant={releaseActive ? 'filled' : 'outlined'}
                label={releaseActive
                  ? (inFlight.length > 0 ? `Running · ${inFlight.length} in flight` : 'Running')
                  : (stats.controlState === 'PAUSED' ? 'Paused' : `Idle · ${num(queued)} ready`)} />
              <Tooltip title={releaseActive
                ? 'A run is already in progress — pause or stop it first'
                : `Run ${runMode === 'materialize' ? 'Materialize' : 'Release'} on ${scope === 'all' ? `all ${rowsTotal.toLocaleString()} filtered` : `${selectionCount.toLocaleString()} selected`}, batch by batch`}>
                <span><IconButton color="success" onClick={doRun}
                  disabled={busy || releaseActive || (scope === 'selected' && selected.size === 0)}>
                  <PlayArrowIcon /></IconButton></span>
              </Tooltip>
              <Tooltip title="Pause release">
                <span><IconButton color="warning" onClick={() => doControl('pause')} disabled={busy || !releaseActive}><PauseIcon /></IconButton></span>
              </Tooltip>
              <Tooltip title="Stop release">
                <span><IconButton color="error" onClick={() => doControl('stop')} disabled={busy || !releaseActive}><StopIcon /></IconButton></span>
              </Tooltip>
            </CardContent>
          </Card>

          {/* Rows */}
          <Card variant="outlined">
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5, flexWrap: 'wrap' }}>
              <TextField size="small" placeholder="Search PN / MPN / manufacturer" value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') applySearch(); }}
                onBlur={applySearch}
                InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
                sx={{ minWidth: 280 }} />
              <TextField select size="small" label="Tier" value={rowTier}
                onChange={(e) => { setRowTier(e.target.value); setPage(0); clearSelection(); }} sx={{ width: 120 }}>
                <MenuItem value="">All tiers</MenuItem>
                {['A', 'B', 'C', 'D', 'E', 'CLARIFICATION'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Status" value={rowStatus}
                onChange={(e) => { setRowStatus(e.target.value); setPage(0); clearSelection(); }} sx={{ width: 150 }}>
                <MenuItem value="">All statuses</MenuItem>
                {Object.keys(stats.byStatus).map((st) => <MenuItem key={st} value={st}>{st}</MenuItem>)}
              </TextField>
              <Box flex={1} />
              <Typography variant="caption" color="text.secondary">{rowsTotal.toLocaleString()} rows</Typography>
            </Stack>
            <Divider />
            {/* Select-all-matching banner (Gmail-style) */}
            {selected.size > 0 && (
              <Box sx={{ px: 2, py: 0.75, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {selected.size.toLocaleString()} row{selected.size === 1 ? '' : 's'} checked — set <b>Run on</b> to “Selected”, or{' '}
                  <Link component="button" onClick={clearSelection}>clear</Link> to run the whole filter.
                </Typography>
              </Box>
            )}
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={pageAllChecked}
                        indeterminate={!pageAllChecked && rows.some((r) => selected.has(r._id))}
                        onChange={(e) => togglePage(e.target.checked)} />
                    </TableCell>
                    {['Material', 'MPN', 'Manufacturer', 'Tier', 'Status', 'PDF', 'Host'].map((h) => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: 'text.secondary' }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => {
                    const checked = selected.has(r._id);
                    return (
                      <TableRow key={r._id} hover selected={checked}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={checked} onChange={() => toggleRow(r._id)} />
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{r.materialNumber}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{r.manufacturerPartNumber || '—'}</TableCell>
                        <TableCell>{r.manufacturerName || '—'}</TableCell>
                        <TableCell>
                          <Chip size="small" label={r.readinessTier}
                            sx={{ bgcolor: TIER_COLOR[r.readinessTier] || '#64748B', color: '#fff', fontWeight: 700, minWidth: 28 }} />
                        </TableCell>
                        <TableCell><Chip size="small" variant="outlined" color={STATUS_COLOR[r.status] || 'default'} label={r.status} /></TableCell>
                        <TableCell>{r.discovery?.pdfRegistered ? '📄' : '—'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
                          {r.discovery?.host || ''}{r.discovery?.error ? ` · ${r.discovery.error}` : ''}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No rows</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={rowsTotal}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          </Card>
        </>
      )}

      {!loadedJob && !preview && (
        jobs.length > 0 ? (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Recent imports</Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableBody>
                    {jobs.map((j) => (
                      <TableRow key={j.jobId} hover sx={{ cursor: 'pointer' }} onClick={() => setLoadedJob(j.jobId)}>
                        <TableCell>{j.jobName || j.originalFileName || 'Catalog import'}</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>{j.jobId}</TableCell>
                        <TableCell>{(j.stats?.totalRows || 0).toLocaleString()} rows</TableCell>
                        <TableCell><Chip size="small" variant="outlined" label={j.status} /></TableCell>
                        <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{new Date(j.createdAt).toLocaleString()}</TableCell>
                        <TableCell padding="checkbox">
                          <Tooltip title="Delete import">
                            <IconButton size="small" color="error" onClick={(e) => doDeleteJob(e, j.jobId)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Typography color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
            Upload a customer catalog file to start — you'll confirm the column mapping, then stage → materialize → release.
          </Typography>
        )
      )}

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
